/* NIID 首页：支持 Shopify 编辑器局部重载、区块选中及关闭模块。 */
(() => {
  'use strict';
  if (customElements.get('niid-home-carousel')) return;
  class HomeElement extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      this.start();
    }
    on(target, name, fn, options = {}) {
      target?.addEventListener(name, fn, {...options, signal:this.controller.signal});
    }
    disconnectedCallback() {
      this.controller?.abort(); this.controller = null;
      this.stop?.();
    }
  }
  class HomeCarousel extends HomeElement {
    start() {
      const slides = [...this.querySelectorAll('.hero-slide')];
      if (slides.length < 2) return;
      const dots = [...this.querySelectorAll('[data-slide]')];
      const toggle = this.querySelector('[data-hero-toggle]');
      const status = this.querySelector('[data-hero-status]');
      const viewport = this.querySelector('.hero-viewport');
      const motion = matchMedia('(prefers-reduced-motion: reduce)');
      const interval = Math.max(4000, Number(this.dataset.interval) || 6000);
      let current = Math.max(0, slides.findIndex(s => s.classList.contains('is-active')));
      let stopped = motion.matches || this.dataset.autoplay !== 'true';
      let hovering = false, visible = false, transition = 0, touchStart;
      this.animations = [];
      const schedule = () => {
        clearTimeout(this.timer);
        if (!stopped && !hovering && visible && !document.hidden) this.timer = setTimeout(() => show(current + 1, 1, false), interval);
      };
      const setStopped = value => {
        stopped = value;
        toggle.dataset.paused = String(stopped);
        toggle.setAttribute('aria-label', stopped ? 'Play slideshow' : 'Pause slideshow');
        schedule();
      };
      const show = (index, direction, manual = true) => {
        if (manual) setStopped(true);
        const next = (index + slides.length) % slides.length;
        if (next === current) return;
        const previous = slides[current], nextSlide = slides[next], revision = ++transition;
        this.animations.forEach(a => a.cancel());
        slides.forEach((slide, i) => {
          slide.classList.remove('is-leaving');
          slide.classList.toggle('is-active', i === next);
          slide.inert = i !== next;
          if (i === next) slide.removeAttribute('aria-hidden'); else slide.setAttribute('aria-hidden', 'true');
        });
        dots.forEach((dot, i) => dot.setAttribute('aria-pressed', String(i === next)));
        if (previous.contains(document.activeElement)) dots[next].focus({preventScroll:true});
        current = next;
        status.textContent = manual ? nextSlide.getAttribute('aria-label') : '';
        if (!motion.matches) {
          previous.classList.add('is-leaving');
          const options = {duration:450, easing:'cubic-bezier(.22,.61,.36,1)'};
          this.animations = [previous.animate([{transform:'translateX(0)'},{transform:`translateX(${-direction * 100}%)`}], options), nextSlide.animate([{transform:`translateX(${direction * 100}%)`},{transform:'translateX(0)'}], options)];
          Promise.allSettled(this.animations.map(a => a.finished)).then(() => { if (revision === transition) previous.classList.remove('is-leaving'); });
        }
        schedule();
      };
      this.on(this.querySelector('[data-hero-previous]'), 'click', () => show(current - 1, -1));
      this.on(this.querySelector('[data-hero-next]'), 'click', () => show(current + 1, 1));
      dots.forEach((dot, i) => this.on(dot, 'click', () => show(i, i > current ? 1 : -1)));
      this.on(toggle, 'click', () => setStopped(!stopped));
      this.on(this, 'focusin', e => { if (e.target !== toggle) setStopped(true); });
      this.on(this, 'pointerenter', e => { if (e.pointerType === 'mouse') { hovering = true; schedule(); } });
      this.on(this, 'pointerleave', e => { if (e.pointerType === 'mouse') { hovering = false; schedule(); } });
      this.on(viewport, 'pointerdown', e => { if (e.isPrimary && e.pointerType !== 'mouse' && !e.target.closest('a,button')) touchStart = {x:e.clientX,y:e.clientY,id:e.pointerId}; });
      this.on(viewport, 'pointerup', e => {
        if (!touchStart || touchStart.id !== e.pointerId) return;
        const dx = e.clientX - touchStart.x, dy = e.clientY - touchStart.y; touchStart = null;
        if (Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy) * 1.4) show(current + (dx < 0 ? 1 : -1), dx < 0 ? 1 : -1);
      });
      this.on(viewport, 'pointercancel', () => { touchStart = null; });
      this.on(document, 'visibilitychange', schedule);
      this.on(motion, 'change', () => { if (motion.matches) setStopped(true); });
      this.on(document, 'shopify:block:select', e => {
        const index = slides.findIndex(slide => slide.dataset.blockId === e.detail?.blockId);
        if (index >= 0) show(index, index >= current ? 1 : -1);
      });
      this.observer = new IntersectionObserver(([entry]) => { visible = entry.isIntersecting && entry.intersectionRatio >= .25; schedule(); }, {threshold:[0,.25]});
      this.observer.observe(this); setStopped(stopped);
    }
    stop() { clearTimeout(this.timer); this.observer?.disconnect(); this.animations?.forEach(a => a.cancel()); }
  }
  class HomeFinder extends HomeElement {
    start() {
      this.on(this, 'click', e => {
        const link = e.target.closest('[data-home-pick]');
        if (!link || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;
        const picks = document.querySelector('niid-home-picks');
        if (picks?.filter(link.dataset.homePick, true)) e.preventDefault();
      });
    }
  }
  class HomePicks extends HomeElement {
    start() {
      this.filters = [...this.querySelectorAll('[data-filter]')];
      this.cards = [...this.querySelectorAll('.product-card')];
      this.link = this.querySelector('[data-collection-link]');
      this.originalHref = this.link?.getAttribute('href');
      this.filters.forEach(button => this.on(button, 'click', () => this.filter(button.dataset.filter)));
    }
    filter(id, scroll = false) {
      const active = this.filters.find(button => button.dataset.filter === id);
      if (!active) return false;
      this.filters.forEach(button => button.setAttribute('aria-pressed', String(button === active)));
      this.cards.forEach(card => { card.hidden = id !== 'all' && card.dataset.category !== id; });
      const count = this.cards.filter(card => !card.hidden).length;
      this.querySelector('[data-filter-status]').textContent = `${count} featured ${count === 1 ? 'product' : 'products'}`;
      if (this.link) this.link.href = active.dataset.collection || this.originalHref;
      if (scroll) { active.focus({preventScroll:true}); this.scrollIntoView({behavior:matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth',block:'start'}); }
      return true;
    }
  }
  class HomeStory extends HomeElement {
    start() {
      this.dialog = this.querySelector('dialog');
      const button = this.querySelector('[data-video]');
      if (!button || !this.dialog) return;
      this.on(button, 'click', () => {
        const id = button.dataset.video;
        if (!/^[a-zA-Z0-9_-]{11}$/.test(id) || this.dialog.open) return;
        const frame = document.createElement('iframe');
        frame.title = this.querySelector('.film-label')?.textContent || 'NIID product film';
        frame.allow = 'autoplay; encrypted-media; picture-in-picture; fullscreen';
        frame.allowFullscreen = true;
        frame.referrerPolicy = 'strict-origin-when-cross-origin';
        frame.src = `https://www.youtube-nocookie.com/embed/${id}?autoplay=1&rel=0&playsinline=1`;
        this.dialog.querySelector('.video-player').replaceChildren(frame);
        this.previousOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        this.trigger = button; this.dialog.showModal();
      });
      this.on(this.dialog.querySelector('[data-close]'), 'click', () => this.dialog.close());
      this.on(this.dialog, 'click', e => {
        if (e.target !== this.dialog) return;
        const r = this.dialog.getBoundingClientRect();
        if (e.clientX < r.left || e.clientX > r.right || e.clientY < r.top || e.clientY > r.bottom) this.dialog.close();
      });
      this.on(this.dialog, 'close', () => { this.release(); this.trigger?.focus({preventScroll:true}); });
    }
    release() {
      this.dialog?.querySelector('.video-player').replaceChildren();
      if (this.previousOverflow !== undefined) { document.body.style.overflow = this.previousOverflow; this.previousOverflow = undefined; }
    }
    stop() { this.dialog?.close(); this.release(); }
  }
  customElements.define('niid-home-carousel', HomeCarousel);
  customElements.define('niid-home-finder', HomeFinder);
  customElements.define('niid-home-picks', HomePicks);
  customElements.define('niid-home-story', HomeStory);
})();
